import { memo, PropsWithoutRef, useCallback } from 'react'

import { AutoDeleteOutlined, Delete } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { AudioObject } from '@nativewaves/nw-cloud-sdk/content'
import { Row, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { useAudioObjectDeleteMutation } from 'hooks/mutations/content'
import { useConfirm } from 'hooks/utils'

type SelectionToolbarActionProps = {
  selectedRows: Row<AudioObject>[]
  tableInstance: Table<AudioObject>
  onLifecyclePolicyClick?: () => void
}

const SelectionToolbarActionsRaw = ({
  selectedRows,
  tableInstance,
  onLifecyclePolicyClick,
}: PropsWithoutRef<SelectionToolbarActionProps>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { mutateAsync: deleteAudioObject } = useAudioObjectDeleteMutation()
  const confirm = useConfirm()

  const handleDeleteSelectedAudioObjects = useCallback(async () => {
    try {
      await confirm(t('areYouSure'))
      await Promise.allSettled(selectedRows.map((row) => deleteAudioObject({ audioId: row.original.id })))
      tableInstance.resetRowSelection()
    } catch (_) {}
  }, [confirm, deleteAudioObject, selectedRows, t, tableInstance])

  return (
    <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Tooltip title={t('domain:Workspace.Audio.setLifecyclePolicy', { count: selectedRows.length })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 0.5, my: 'auto' }} size="medium" onClick={onLifecyclePolicyClick}>
          <AutoDeleteOutlined fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('domain:Workspace.Audio.deleteAudioObject', { count: selectedRows.length })} placement="top">
        <IconButton sx={{ ml: 'auto', mr: 2, my: 'auto' }} size="medium" onClick={handleDeleteSelectedAudioObjects}>
          <Delete fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SelectionToolbarActions = memo(SelectionToolbarActionsRaw)
